import { t } from '@lingui/macro';

import { ServiceType } from '@rover/constants/src/translations/service.constants';
import Boarding from '@rover/icons/svg/boarding.svg';
import Daycare from '@rover/icons/svg/daycare.svg';
import DogTraining from '@rover/icons/svg/dog-training.svg';
import DropIn from '@rover/icons/svg/drop-in.svg';
import Grooming from '@rover/icons/svg/grooming.svg';
import PetInsuranceAlert from '@rover/icons/svg/pet-insurance-alert.svg';
import Sitting from '@rover/icons/svg/sitting.svg';
import Walking from '@rover/icons/svg/walking.svg';
import getBrandSpecificValue from '@rover/shared/js/utils/getBrandSpecificValue';

import boardingImage from './assets/boarding.jpg';
import dogWalkingImage from './assets/dog-walking.jpg';
import doggyDayCareImage from './assets/doggy-day-care.jpg';
import dropInVisitsImage from './assets/drop-in-visits.jpg';
import groomingImage from './assets/grooming.jpg';
import houseSittingImage from './assets/house-sitting.jpg';
import petInsuranceImage from './assets/pet-insurance.jpg';
import trainingImage from './assets/training.jpg';
import type { CatalogCategory, CatalogEntry } from './types';

/** The brand-aware copy block of an entry, resolved once per entry via getBrandSpecificValue. */
type CatalogCopy = Pick<
  CatalogEntry,
  'serviceSlug' | 'name' | 'location' | 'for' | 'description' | 'cta' | 'imageUrl'
>;

/**
 * The static, declarative service catalog.
 *
 * Built by a function so `t` and getBrandSpecificValue resolve at call time
 * (per locale / per brand).
 */
export const getServiceCatalogEntries = (): CatalogEntry[] => {
  const overnight: CatalogCategory = { id: 'overnight', label: t`Overnight care` };
  const daytime: CatalogCategory = { id: 'daytime', label: t`Daytime care` };
  const other: CatalogCategory = { id: 'other', label: t`Other services` };

  return [
    {
      serviceType: ServiceType.OVERNIGHT_BOARDING,
      icon: { component: Boarding, colorScheme: 'blue' },
      category: overnight,
      ...getBrandSpecificValue<CatalogCopy>({
        rover: {
          serviceSlug: 'dog-boarding',
          name: t`Boarding`,
          location: t`In sitter's home`,
          for: t`For dogs & cats`,
          description: t`Book a cozy sleepover at a trusted sitter's house. Your pet gets personalized care. You get photo updates and can rest easy knowing they're safe and happy.`,
          cta: t`Explore Boarding`,
          imageUrl: boardingImage,
        },
      }),
    },
    {
      serviceType: ServiceType.OVERNIGHT_TRAVELING,
      icon: { component: Sitting, colorScheme: 'blue' },
      category: overnight,
      ...getBrandSpecificValue<CatalogCopy>({
        rover: {
          serviceSlug: 'house-sitting',
          name: t`House Sitting`,
          location: t`In your home`,
          for: t`For dogs & cats`,
          description: t`Travel stress-free knowing your pet and home are being looked after. Book a trusted house sitter on Rover to stay in your home and keep your pet's routine.`,
          cta: t`Explore House Sitting`,
          imageUrl: houseSittingImage,
        },
      }),
    },
    {
      serviceType: ServiceType.DROP_IN,
      icon: { component: DropIn, colorScheme: 'orange' },
      category: daytime,
      ...getBrandSpecificValue<CatalogCopy>({
        rover: {
          serviceSlug: 'pet-sitting',
          name: t`Drop-In Visits`,
          location: t`In your home`,
          for: t`For dogs & cats`,
          description: t`Drop-in visits are perfect for cats and independent pups who just need a sitter to drop by your home one or more times a day. Your pets can get potty breaks, a clean litter box, playtime, meals, and more.`,
          cta: t`Explore Pet Sitting`,
          imageUrl: dropInVisitsImage,
        },
      }),
    },
    {
      serviceType: ServiceType.DOG_WALKING,
      icon: { component: Walking, colorScheme: 'orange' },
      category: daytime,
      ...getBrandSpecificValue<CatalogCopy>({
        rover: {
          serviceSlug: 'dog-walking',
          name: t`Dog Walking`,
          location: t`In your neighborhood`,
          for: t`For dogs`,
          description: t`Give active pups exercise, potty breaks, and fresh air with a trusted dog walker. Get updates, photos, and a map of the route after every walk.`,
          cta: t`Explore Dog Walking`,
          imageUrl: dogWalkingImage,
        },
      }),
    },
    {
      serviceType: ServiceType.DOGGY_DAY_CARE,
      icon: { component: Daycare, colorScheme: 'orange' },
      category: daytime,
      ...getBrandSpecificValue<CatalogCopy>({
        rover: {
          serviceSlug: 'doggy-day-care',
          name: t`Doggy Day Care`,
          location: t`In the sitter's home`,
          for: t`For dogs`,
          description: t`Give your dog a day of play at a trusted sitter's house. Drop them off in the morning and pick up a happy pup in the evening.`,
          cta: t`Explore Doggy Day Care`,
          imageUrl: doggyDayCareImage,
        },
      }),
    },
    {
      serviceType: ServiceType.GROOMING,
      href: '/grooming/search/',
      icon: { component: Grooming, colorScheme: 'pink' },
      category: other,
      ...getBrandSpecificValue<CatalogCopy>({
        rover: {
          serviceSlug: 'grooming',
          name: 'Grooming',
          location: 'In your neighborhood',
          for: 'For dogs',
          description:
            'Your groomer bathes and grooms your pet at their home or yours. Your pet can get a full-service groom in a familiar environment.',
          cta: 'Explore Grooming',
          imageUrl: groomingImage,
        },
      }),
    },
    {
      serviceType: ServiceType.DOG_TRAINING,
      icon: { component: DogTraining, colorScheme: 'pink' },
      category: other,
      ...getBrandSpecificValue<CatalogCopy>({
        rover: {
          serviceSlug: 'dog-training',
          name: 'Dog Training',
          location: null,
          for: 'For dogs and puppies',
          description:
            'Book a private in-person dog trainer to shape behaviors and learn new skills with your puppy or adult dog. Find the right trainer near you to help with potty training, leash manners, separation distress and more.',
          cta: 'Explore Training',
          imageUrl: trainingImage,
        },
      }),
    },
    {
      href: '/lemonade-pet-insurance/?source=homepage',
      icon: { component: PetInsuranceAlert, colorScheme: 'pink' },
      category: other,
      ...getBrandSpecificValue<CatalogCopy>({
        rover: {
          serviceSlug: 'pet-insurance',
          name: 'Pet Insurance',
          location: null,
          for: 'For dogs & cats',
          description:
            "Explore pet insurance with our partner, Lemonade, to get a plan that gives your pet the care they need. You get peace of mind knowing you're financially prepared for any unexpected vet bills.",
          cta: 'Browse Pet Insurance',
          imageUrl: petInsuranceImage,
        },
      }),
    },
  ];
};
